import { toast } from 'sonner';
import { FaHashtag, FaThumbtack } from '../ui/FaIcons';
import { usePinnedMessages, useSetMessagePinned } from '../../hooks/queries/useMessaging';
import { useState, useEffect, useRef, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, X, Settings, Users, MessageCircle, Upload } from 'lucide-react';
import { MessageList } from './MessageList';
import { MessageComposer, type MessageComposerHandle } from './MessageComposer';
import { useWizz } from '../../hooks/queries/useWizz';
import { cn } from '../../lib/cn';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import { ChannelMembersModal } from './ChannelMembersModal';
import { useAuthStore } from '../../store/useAuthStore';
import { useSidecarStore } from '../../store/useSidecarStore';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import {
  useMessages,
  useLoadOlderMessages,
  useMessagingRealtime,
  useSendMessage,
  useEditMessage,
  useDeleteMessage,
  useMarkChannelAsRead,
  useCollaboratorsForMessaging,
  useTypingPresence,
  useDmReadReceipt,
  useMessageAttachments,
  useMessageReactions,
  useToggleReaction,
  useReactionsRealtime,
  useAttachmentsRealtime,
  useDeleteMessageAttachment,
  type Channel,
  type Message,
} from '../../hooks/queries/useMessaging';
import type { MessageAttachment } from '../../services/messaging/messageAttachmentService';

interface ChannelViewProps {
  channel: Channel;
  /** Salon de dossier : épinglage des messages et bandeau du dernier épinglé. */
  pinnable?: boolean;
  /** Salon de dossier : en-tête court (nom du salon et précision), sur fond blanc. */
  compactHeader?: { title: string; note: string };
}

export function ChannelView({ channel, pinnable = false, compactHeader }: ChannelViewProps) {
  const { data: pinned = [] } = usePinnedMessages(pinnable ? channel.id : undefined);
  const setPinned = useSetMessagePinned();
  const lastPinned = pinned[0];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrolledRef = useRef(false);

  const collaborator = useAuthStore((s) => s.collaborator);
  const canManage = canAccessFeature(asCollaboratorRole(collaborator?.role), 'messaging_manage_channel');
  const composerRef = useRef<MessageComposerHandle | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Capture lastReadAt au premier rendu (avant que markAsRead ne le bump).
  const initialUnreadSinceRef = useRef<string | null | undefined>(channel.lastReadAt);

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    if (editingId) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }
  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (editingId) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  }
  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  }
  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (editingId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      composerRef.current?.addFiles(e.dataTransfer.files);
    }
  }

  const { data: messages = [], isLoading } = useMessages(channel.id);
  const { loadOlder, isLoadingOlder, hasMore } = useLoadOlderMessages(channel.id);
  const { data: collaborators = [] } = useCollaboratorsForMessaging();
  const { data: attachmentsByMessage } = useMessageAttachments(
    channel.id,
    messages.map((m) => m.id),
  );
  const { data: reactionsByMessage } = useMessageReactions(
    channel.id,
    messages.map((m) => m.id),
  );
  const toggleReaction = useToggleReaction();
  useReactionsRealtime(channel.id);
  useAttachmentsRealtime(channel.id);
  const sendMessage = useSendMessage();
  const editMessage = useEditMessage();
  const deleteMessage = useDeleteMessage();
  const deleteAttachment = useDeleteMessageAttachment();

  function handleDeleteAttachment(att: MessageAttachment) {
    deleteAttachment.mutate({
      attachmentId: att.id,
      storagePath: att.storage_path,
      channelId: channel.id,
      messageId: att.message_id,
    });
  }
  const markAsRead = useMarkChannelAsRead();

  useMessagingRealtime(channel.id);
  const { typingUsers, startTyping, stopTyping } = useTypingPresence(channel.id);
  const { isShaking, canWizz, sendWizz } = useWizz(channel.id);
  const dmOtherLastReadAt = useDmReadReceipt(channel.id, channel.kind === 'dm');

  // Mark as read when channel opens or new messages arrive
  useEffect(() => {
    markAsRead(channel.id);
  }, [channel.id, messages.length, markAsRead]);

  // Reset auto-scroll flag when changing channel (sidecar reuses ChannelView)
  useEffect(() => {
    scrolledRef.current = false;
  }, [channel.id]);

  // Scroll to a specific message when arriving from a notification link.
  // Source : searchParams (?messageId=...) OU sidecar store (pendingMessageId).
  const pendingMessageId = useSidecarStore((s) => s.pendingMessageId);
  const consumePendingMessageId = useSidecarStore((s) => s.consumePendingMessageId);
  useEffect(() => {
    const messageId = searchParams.get('messageId') ?? pendingMessageId;
    if (!messageId || isLoading || scrolledRef.current) return;
    const el = document.getElementById(messageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('bg-cime-gold/20');
      setTimeout(() => el.classList.remove('bg-cime-gold/20'), 2000);
      scrolledRef.current = true;
      if (searchParams.get('messageId')) setSearchParams({}, { replace: true });
      if (pendingMessageId) consumePendingMessageId();
    }
  }, [messages, isLoading, searchParams, setSearchParams, pendingMessageId, consumePendingMessageId]);

  const channelTitle =
    channel.kind === 'dm'
      ? channel.dmOtherCollaborator?.full_name ?? 'Message direct'
      : `#${channel.name ?? 'canal'}`;

  const channelSubtitle =
    channel.kind === 'global'
      ? 'Canal global — tous les membres du cabinet'
      : 'Message direct';

  function handleSend(body: string, attachments?: File[]) {
    sendMessage.mutate({
      channelId: channel.id,
      body,
      parentId: replyTo?.id,
      attachments,
    });
    setReplyTo(null);
  }

  function handleEditConfirm(body: string) {
    if (!editingId) return;
    editMessage.mutate({ id: editingId, body, channelId: channel.id });
    setEditingId(null);
  }

  function handleDelete(id: string) {
    deleteMessage.mutate({ id, channelId: channel.id });
  }

  function handleStartEdit(id: string, currentBody: string) {
    setEditingId(id);
    setEditingBody(currentBody);
    setReplyTo(null);
  }

  function handleCancelEdit() {
    setEditingId(null);
  }

  return (
    <div className={cn('flex flex-col h-full', isShaking && 'cime-wizz-shake')}>
      {/* Header */}
      {compactHeader ? (
        <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-[#e6e2d8] bg-white px-[18px] py-3">
          <FaHashtag className="h-4 w-4 flex-shrink-0 text-cime-gold" aria-hidden />
          <b className="text-sm font-semibold text-cime-text-primary">{compactHeader.title}</b>
          <small className="truncate text-xs text-neutral-400">· {compactHeader.note}</small>
        </div>
      ) : (
      <div className="flex items-center gap-3 px-4 py-3 border-b border-cime-border bg-cime-bg flex-shrink-0">
        {channel.kind === 'dm' ? (
          channel.dmOtherCollaborator?.id ? (
            <CollaboratorLink collaboratorId={channel.dmOtherCollaborator.id} title={`Voir le profil de ${channel.dmOtherCollaborator.full_name}`}>
              {channel.dmOtherCollaborator.avatar_url ? (
                <img
                  src={channel.dmOtherCollaborator.avatar_url}
                  alt={channel.dmOtherCollaborator.full_name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-cime-primary text-white flex items-center justify-center text-sm font-medium">
                  {channel.dmOtherCollaborator.full_name.charAt(0).toUpperCase()}
                </span>
              )}
            </CollaboratorLink>
          ) : (
            channel.dmOtherCollaborator?.avatar_url ? (
              <img
                src={channel.dmOtherCollaborator.avatar_url}
                alt={channel.dmOtherCollaborator.full_name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <span className="w-8 h-8 rounded-full bg-cime-primary text-white flex items-center justify-center text-sm font-medium">
                {(channel.dmOtherCollaborator?.full_name ?? 'D').charAt(0).toUpperCase()}
              </span>
            )
          )
        ) : (
          <div className="w-8 h-8 rounded-lg bg-cime-primary/10 flex items-center justify-center">
            <img
              src={`/icons/${channel.isPrivate ? 'lock-hashtag' : (channel.emoji ?? 'hashtag')}.svg`}
              alt=""
              className="w-4 h-4 opacity-60"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-cime-text-primary">
            {channel.kind === 'dm' && channel.dmOtherCollaborator?.id ? (
              <CollaboratorLink collaboratorId={channel.dmOtherCollaborator.id} title={`Voir le profil de ${channel.dmOtherCollaborator.full_name}`}>{channelTitle}</CollaboratorLink>
            ) : channelTitle}
          </h2>
          <p className="text-xs text-cime-text-muted">{channelSubtitle}</p>
        </div>

        {/* Actions admin — canaux globaux uniquement */}
        {canManage && channel.kind === 'global' && (
          <div className="flex items-center gap-1">
            {channel.isPrivate && (
              <button
                type="button"
                onClick={() => setShowMembers(true)}
                className="p-1.5 rounded-lg text-cime-text-muted hover:bg-cime-sidebar-active-bg hover:text-cime-text-primary transition-colors"
                title="Gérer les membres"
              >
                <Users className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="p-1.5 rounded-lg text-cime-text-muted hover:bg-cime-sidebar-active-bg hover:text-cime-text-primary transition-colors"
              title="Paramètres du canal"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      )}

      {/* Drop zone wrapping messages + composer */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="relative flex flex-col flex-1 min-h-0"
      >
        {dragOver && (
          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/95 border-2 border-dashed border-cime-primary rounded-lg pointer-events-none">
            <Upload className="w-8 h-8 text-cime-primary mb-2" />
            <span className="text-sm font-medium text-cime-primary">
              Déposez les fichiers pour les joindre
            </span>
            <span className="text-xs text-cime-text-muted mt-0.5">
              Jusqu'à 10 fichiers, 10 Mo chacun
            </span>
          </div>
        )}

        {lastPinned ? (
          <div className="flex items-center gap-2 border-b border-[#e6e2d8] bg-[#fbf6e6] px-4 py-2 text-[13px] text-cime-text-primary">
            <FaThumbtack className="h-3 w-3 flex-shrink-0 text-cime-gold-text" aria-hidden />
            <b className="flex-shrink-0 font-semibold">Épinglé</b>
            <span className="min-w-0 truncate text-cime-text-muted">
              · {lastPinned.body}
              {lastPinned.author?.full_name ? ` — ${lastPinned.author.full_name}` : ''}
            </span>
          </div>
        ) : null}

        {/* Messages */}
        <MessageList
          messages={messages}
          isLoading={isLoading}
          onLoadOlder={loadOlder}
          isLoadingOlder={isLoadingOlder}
          hasMore={hasMore}
          onDelete={handleDelete}
          onEdit={handleStartEdit}
          onReply={(msg) => setReplyTo(msg)}
          onTogglePin={
            pinnable
              ? (msg) =>
                  setPinned.mutate(
                    { id: msg.id, channelId: channel.id, pinned: !msg.pinnedAt },
                    { onError: (e) => toast.error(e instanceof Error ? e.message : 'Impossible d’épingler') },
                  )
              : undefined
          }
          dmOtherLastReadAt={channel.kind === 'dm' ? dmOtherLastReadAt : null}
          attachmentsByMessage={attachmentsByMessage}
          onDeleteAttachment={handleDeleteAttachment}
          reactionsByMessage={reactionsByMessage}
          onToggleReaction={(messageId, emoji, hasReacted) =>
            toggleReaction.mutate({ messageId, emoji, channelId: channel.id, hasReacted })
          }
          onJumpToMessage={(messageId) => {
            const el = document.getElementById(messageId);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('bg-cime-gold/20');
            setTimeout(() => el.classList.remove('bg-cime-gold/20'), 2000);
          }}
          initialUnreadSince={initialUnreadSinceRef.current}
        />

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <div className="px-4 py-1 text-xs text-cime-text-muted flex items-center gap-1.5">
          <span className="flex gap-0.5 items-end h-3">
            <span className="w-1 h-1 rounded-full bg-cime-text-muted animate-bounce [animation-delay:0ms]" />
            <span className="w-1 h-1 rounded-full bg-cime-text-muted animate-bounce [animation-delay:150ms]" />
            <span className="w-1 h-1 rounded-full bg-cime-text-muted animate-bounce [animation-delay:300ms]" />
          </span>
          <span>
            {typingUsers.length === 1
              ? `${typingUsers[0]} est en train d'écrire…`
              : `${typingUsers.slice(0, -1).join(', ')} et ${typingUsers[typingUsers.length - 1]} écrivent…`}
          </span>
        </div>
      )}

      {/* Reply banner */}
      {replyTo && (
        <div className="flex items-center gap-2 px-4 py-2 bg-cime-surface border-t border-cime-border text-xs text-cime-text-muted">
          <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate flex-1">
            Réponse à <strong>{replyTo.author?.full_name ?? 'utilisateur'}</strong> : {replyTo.body.slice(0, 60)}
            {replyTo.body.length > 60 && '…'}
          </span>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            className="flex-shrink-0 hover:text-cime-text-primary transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Edit mode */}
      {editingId ? (
        <div className="p-3 border-t border-cime-border bg-cime-surface">
          <div className="flex items-center gap-2 mb-2 text-xs text-cime-text-muted">
            <Pencil className="w-3.5 h-3.5" />
            <span>Modification du message</span>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="ml-auto flex items-center gap-1 hover:text-cime-text-primary transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Annuler
            </button>
          </div>
          <MessageComposer
            key={editingId}
            onSend={handleEditConfirm}
            disabled={editMessage.isPending}
            collaborators={collaborators}
            placeholder="Modifier le message…"
            channelId={channel.id}
            onStartTyping={startTyping}
            onStopTyping={stopTyping}
            allowAttachments={false}
            initialValue={editingBody}
            useDraft={false}
          />
        </div>
      ) : (
        <MessageComposer
          ref={composerRef}
          onSend={handleSend}
          disabled={sendMessage.isPending}
          collaborators={collaborators}
          placeholder={`Message ${channelTitle}`}
          channelId={channel.id}
          onStartTyping={startTyping}
          onStopTyping={stopTyping}
          onWizz={sendWizz}
          wizzDisabled={!canWizz}
        />
      )}
      </div>

      {showSettings && (
        <ChannelSettingsModal
          mode="edit"
          channel={channel}
          onClose={() => setShowSettings(false)}
          onUpdated={() => setShowSettings(false)}
          onDeleted={() => setShowSettings(false)}
        />
      )}

      {showMembers && (
        <ChannelMembersModal
          channel={channel}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}
